import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import {
  Card,
  CardContent,
  TextField,
  Grid,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import { useGlobalMDAlert } from "context";
import { FP, fpInputSx } from "components/financepro";
import { useCreds, postDO } from "./common";

const fmtNum = (n) => Number(n || 0).toLocaleString("es-DO");

// El backend devuelve "YYYY-MM-DD HH:MM:SS". Lo mostramos en formato amable.
function fmtFecha(s) {
  if (!s) return "—";
  const d = new Date(String(s).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("es-DO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Pestaña "Aportar Datos": vuelca la cartera del tenant a la base conjunta.
export function EnviarDatos() {
  const creds = useCreds();
  const { showAlert } = useGlobalMDAlert();
  const [nosucursal, setNosucursal] = useState(null);
  const [sucursal, setSucursal] = useState([]);
  const [alcance, setAlcance] = useState("todos"); // "todos" | "atrasados"
  const [cargando, setCargando] = useState(false);
  const [ultimo, setUltimo] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [cartera, setCartera] = useState({ total: 0, vivo: 0, aporte: 0 });
  // Con el portal del negocio encendido la red le lee la cartera sola y
  // este envío deja de ser la vía principal.
  const [lecturaViva, setLecturaViva] = useState(false);

  const cargarHistorial = () => {
    postDO("/api/dataorbit_historial", {
      usuario: creds.usuario,
      password: creds.password,
      nombrenegocio: creds.nombrenegocio,
    }).then(({ ok, data }) => {
      if (ok) {
        setHistorial(data.historial || []);
        setCartera(data.cartera || { total: data.total_prestamos || 0, vivo: 0, aporte: 0 });
        setLecturaViva(!!data.lectura_viva);
      }
    });
  };

  useEffect(() => {
    postDO("/api/recuperasucursales", { usuario: creds.usuario, password: creds.password }).then(({ ok, data }) => {
      if (ok) setSucursal([{ label: "0-TODOS" }, ...(data.sucursales || [])]);
    });
    cargarHistorial();
  }, []);

  function enviar() {
    const sucLabel = nosucursal?.label || "";
    const sucParam = !sucLabel || sucLabel.toUpperCase().includes("TODO") ? "" : sucLabel.split("-")[0];
    setCargando(true);
    postDO("/api/dataorbit_enviar", {
      usuario: creds.usuario,
      password: creds.password,
      nombrenegocio: creds.nombrenegocio,
      sucursal: sucParam,
      soloatrasados: alcance === "atrasados" ? 1 : 0,
    })
      .then(({ ok, data }) => {
        if (ok) {
          setUltimo(data);
          cargarHistorial();
          showAlert({ message: `Se aportaron ${data.enviados} registros a DataOrbit`, color: "success", autoHideDuration: 3500 });
        } else {
          showAlert({ message: data.Error || "No se pudo enviar", color: "error", autoHideDuration: 3500 });
        }
      })
      .finally(() => {
        setCargando(false);
      });
  }

  return (
    <>
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.cardBg, boxShadow: FP.shadow.soft, overflow: "hidden" }}>
        {/* Cabecera con degradado navy */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.8,
            px: { xs: 2.5, md: 3 },
            py: 2.5,
            background: `linear-gradient(135deg, ${FP.color.navy} 0%, ${FP.color.navySoft} 100%)`,
          }}
        >
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.12)",
              color: "#FFFFFF",
            }}
          >
            <HubRoundedIcon />
          </Box>
          <Box>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: "1.05rem", lineHeight: 1.2 }}>
              {lecturaViva ? "Tu cartera se lee sola" : "Aportar cartera a DataOrbit"}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.8rem" }}>
              {lecturaViva
                ? "La red consulta tus préstamos en vivo cuando alguien pregunta por un cliente tuyo."
                : "Reenviar no duplica: actualiza los préstamos ya enviados."}
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              gap: 1.2,
              p: 1.6,
              mb: 2.5,
              borderRadius: 2,
              border: `1px solid ${lecturaViva ? FP.color.successDark : FP.color.border}22`,
              bgcolor: lecturaViva ? FP.color.successSoft : FP.color.surfaceAlt,
            }}
          >
            {lecturaViva ? (
              <CloudDoneRoundedIcon sx={{ fontSize: 19, color: FP.color.successDark, flexShrink: 0 }} />
            ) : (
              <InfoOutlinedIcon sx={{ fontSize: 19, color: FP.color.textSecondary, flexShrink: 0 }} />
            )}
            <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary, lineHeight: 1.5 }}>
              {lecturaViva ? (
                <>
                  No hace falta que envíes nada para que la red te vea al día. El envío sigue
                  sirviendo para la <strong>Depuración Masiva</strong>, que se responde desde lo
                  ya guardado y no lee en vivo.
                </>
              ) : (
                <>
                  Hoy la red solo conoce lo que envías desde aquí. Si enciendes el{" "}
                  <strong>portal de clientes</strong> en Configuración, tu cartera se leerá sola
                  y dejará de depender de estos envíos.
                </>
              )}
            </Typography>
          </Box>

          <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.82rem", fontWeight: 700, mb: 1.2 }}>
            ¿Qué préstamos deseas aportar?
          </Typography>
          <ToggleButtonGroup
            value={alcance}
            exclusive
            onChange={(_, v) => v && setAlcance(v)}
            sx={{
              mb: 2.5,
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              "& .MuiToggleButtonGroup-grouped": {
                flex: { xs: "1 1 100%", sm: "1 1 0" },
                border: `1px solid ${FP.color.border}`,
                borderRadius: "10px !important",
                textTransform: "none",
                color: FP.color.textSecondary,
                justifyContent: "flex-start",
                gap: 1,
                px: 2,
                py: 1.2,
                "&.Mui-selected": {
                  bgcolor: FP.color.accentBlueSoft,
                  borderColor: FP.color.accentBlue,
                  color: FP.color.accentBlue,
                  "&:hover": { bgcolor: FP.color.accentBlueSoft },
                },
              },
            }}
          >
            <ToggleButton value="todos">
              <ListAltRoundedIcon sx={{ fontSize: 20 }} />
              <Box sx={{ textAlign: "left" }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, lineHeight: 1.2 }}>Todos los préstamos</Typography>
                <Typography sx={{ fontSize: "0.72rem", opacity: 0.85 }}>Aporta la cartera completa</Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="atrasados">
              <ReportProblemRoundedIcon sx={{ fontSize: 20 }} />
              <Box sx={{ textAlign: "left" }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, lineHeight: 1.2 }}>Solo atrasados</Typography>
                <Typography sx={{ fontSize: "0.72rem", opacity: 0.85 }}>Únicamente préstamos en mora</Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                disablePortal
                options={sucursal || []}
                value={nosucursal}
                getOptionLabel={(o) => (typeof o === "string" ? o : o?.label || "")}
                isOptionEqualToValue={(o, v) => (o?.label || o) === (v?.label || v)}
                onChange={(_, v) => setNosucursal(v || null)}
                renderInput={(params) => <TextField {...params} label="Sucursal" fullWidth InputLabelProps={{ shrink: true }} sx={fpInputSx} />}
                fullWidth
              />
            </Grid>
          </Grid>
          {cargando && (
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary, mb: 0.6 }}>
                Enviando {alcance === "atrasados" ? "préstamos atrasados" : "todos los préstamos"} a la base conjunta…
              </Typography>
              <LinearProgress
                variant="indeterminate"
                sx={{
                  height: 8,
                  borderRadius: 5,
                  bgcolor: FP.color.border,
                  "& .MuiLinearProgress-bar": { bgcolor: FP.color.navy },
                }}
              />
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
            <Button
              variant="contained"
              disabled={cargando}
              startIcon={<CloudUploadRoundedIcon />}
              onClick={enviar}
              sx={{
                minWidth: { sm: 230 },
                height: 46,
                bgcolor: FP.color.navy,
                color: "#FFFFFF",
                borderRadius: 1.4,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
              }}
            >
              {cargando ? "Enviando..." : "Enviar a base conjunta"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {ultimo && (
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${FP.color.successDark}22`, bgcolor: FP.color.successSoft, boxShadow: "none" }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 }, "&:last-child": { pb: { xs: 2, md: 2.5 } } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.6 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#FFFFFF",
                  color: FP.color.successDark,
                  flexShrink: 0,
                }}
              >
                <CloudDoneRoundedIcon />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "0.95rem" }}>
                  Envío completado · {fmtNum(ultimo.enviados)} registros
                </Typography>
                <Typography sx={{ color: FP.color.successDark, fontSize: "0.8rem", fontWeight: 600 }}>
                  {fmtFecha(ultimo.fecha)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card elevation={0} sx={{ mt: 3, borderRadius: 3, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.cardBg, boxShadow: FP.shadow.soft }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
            <HistoryRoundedIcon sx={{ color: FP.color.navy, fontSize: 22 }} />
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, flexGrow: 1 }}>Tu cartera en la red</Typography>
            <Tooltip title="Actualizar">
              <IconButton size="small" onClick={cargarHistorial} sx={{ color: FP.color.textSecondary }}>
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {cartera.total === 0 && historial.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: FP.color.surfaceAlt,
                  color: FP.color.textMuted,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.5,
                }}
              >
                <HistoryRoundedIcon />
              </Box>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.88rem", fontWeight: 600 }}>
                Todavía no hay préstamos tuyos en la red
              </Typography>
              <Typography sx={{ color: FP.color.textMuted, fontSize: "0.8rem" }}>
                {lecturaViva
                  ? "Aparecerán solos cuando alguien consulte a un cliente tuyo."
                  : "Envía tu cartera desde arriba para que aparezcan aquí."}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Resumen. `vivo` y `aporte` no son lo mismo: uno se refresca
                  solo y el otro depende de que vuelvas a enviarlo. */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                  gap: 1.5,
                  mb: 2.5,
                }}
              >
                {[
                  { icon: Inventory2RoundedIcon, label: "En la red", value: fmtNum(cartera.total) },
                  { icon: BoltRoundedIcon, label: "Se actualizan solos", value: fmtNum(cartera.vivo) },
                  { icon: CloudUploadRoundedIcon, label: "Dependen de tus envíos", value: fmtNum(cartera.aporte) },
                  { icon: ScheduleRoundedIcon, label: "Último envío", value: historial.length ? fmtFecha(historial[0]?.fecha) : "—", small: true },
                ].map((k) => (
                  <Box
                    key={k.label}
                    sx={{
                      border: `1px solid ${FP.color.border}`,
                      borderRadius: 2,
                      p: 1.6,
                      bgcolor: FP.color.surface,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.6, color: FP.color.textSecondary }}>
                      <k.icon sx={{ fontSize: 15 }} />
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        {k.label}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: k.small ? "0.82rem" : "1.25rem", lineHeight: 1.2 }}>
                      {k.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {historial.length > 0 && (
                <Divider sx={{ borderColor: FP.color.border, mb: 1 }} />
              )}

              {/* Timeline: solo los envíos manuales. Lo que la red refresca
                  sola no pasa por aquí. */}
              <Box>
                {historial.map((h, i) => {
                  const ok = h.estado === "ok";
                  return (
                    <Box
                      key={h.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.6,
                        py: 1.4,
                        borderBottom: i < historial.length - 1 ? `1px solid ${FP.color.border}` : "none",
                      }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          bgcolor: ok ? FP.color.successSoft : FP.color.dangerSoft,
                          color: ok ? FP.color.successDark : FP.color.danger,
                        }}
                      >
                        <CloudDoneRoundedIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "0.92rem" }}>
                          {fmtNum(h.registros)} registros aportados
                        </Typography>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.78rem" }}>
                          {fmtFecha(h.fecha)}
                        </Typography>
                      </Box>
                      <Chip
                        label={ok ? "Completado" : (h.estado || "—")}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          bgcolor: ok ? FP.color.successSoft : FP.color.border,
                          color: ok ? FP.color.successDark : FP.color.textSecondary,
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
